<template>
  <div v-if="user" class="subscription-page">
    <div v-if="!user.subscribed && (user.agency && !user.agency.free_fee) && (user.trial_past || hasPastDuePlan) && !recoveryEligible" class="alert alert-danger" role="alert">
      Please subscribe to a package, or update your payment method.
    </div>
    <b-modal
      v-model="showPaymentModal"
      hide-footer
      centered
      no-close-on-backdrop
      no-close-on-esc
      modal-class="subscription-recovery-modal"
      content-class="subscription-recovery-modal__content"
      body-class="subscription-recovery-modal__body"
    >
      <template v-slot:modal-header>
        <div class="subscription-recovery-modal__header">
          <div class="subscription-recovery-modal__header-main">
            <i class="uil uil-exclamation-triangle subscription-recovery-modal__header-icon"></i>
            <div>
            <div class="subscription-recovery-modal__title">{{ recoveryModalHeadline }}</div>
            <div class="subscription-recovery-modal__subtitle">
              {{ recoveryModalSubhead }}
            </div>
            </div>
          </div>
        </div>
      </template>
      <div v-if="recoverablePlan" class="subscription-recovery-modal__plan">
        <div>
          <div class="subscription-recovery-modal__plan-top">
            <span class="subscription-recovery-modal__plan-label">{{ recoveryPlanModalLabel }}</span>
            <span v-if="recoveryPlanStatusBadgeText" class="subscription-recovery-modal__plan-status">{{
              recoveryPlanStatusBadgeText
            }}</span>
          </div>
          <div class="subscription-recovery-modal__plan-name">{{ recoverablePlan.name }}</div>
          <div v-if="recoverablePlan.desc" class="subscription-recovery-modal__plan-desc">{{ recoverablePlan.desc }}</div>
        </div>
        <div class="subscription-recovery-modal__plan-right">
          <div v-if="cardMaskText" class="subscription-recovery-modal__card-mask">{{ cardMaskText }}</div>
          <div v-if="declineMessage" class="subscription-recovery-modal__decline">{{ declineMessage }}</div>
        </div>
      </div>
      <template v-if="recoveryShowsStripeCardForm">
        <div class="subscription-recovery-modal__payment-header">
          <div class="subscription-recovery-modal__payment-title">
            {{ recoveryRequiresPaymentUpdateForm ? 'Update payment method' : 'Add payment method' }}
          </div>
          <div class="subscription-recovery-modal__secure">
            <i class="uil uil-lock-alt"></i> Secured by Stripe
          </div>
        </div>
        <AddCreditCard ref="cardForm" />
        <div class="subscription-recovery-modal__encrypt">
          <i class="uil uil-shield-check"></i> Encrypted and processed securely by Stripe.
        </div>
      </template>
      <button
        class="btn subscription-recovery-modal__cta w-100"
        :disabled="loadingSubscription"
        @click="onRecoveryModalPrimaryAction"
      >
        <b-spinner v-if="loadingSubscription" small />
        <span v-else>{{ modalCtaText }}</span>
      </button>
      <div class="subscription-recovery-modal__note">{{ recoveryModalFootnote }}</div>
    </b-modal>
    <div>
      <div v-if="!loadingPlans" class="row">
        <div v-for="plan in plans" :key="plan.id" :class="{ 'col-lg-3': typeFilter === null, 'col-md-6': typeFilter !== null }">
          <div class="card card-pricing">
            <div class="card-body p-4">
              <div class="media">
                <div class="media-body">
                  <h5 class="mt-0 mb-2 font-size-16">{{ plan.name }}</h5>
                  <h2 class="mt-0 mb-2"
                    >${{ plan.amount }}
                    <span class="font-size-14">/
                      <em v-if="plan.type === 'one_time'">
                      OneTime
                      </em>
                      <em v-else-if="plan.recurring && plan.recurring.interval === 'year' && plan.recurring.interval_count === 1">
                      Yearly
                      </em>
                      <em v-else-if="plan.recurring && plan.recurring.interval === 'month' && plan.recurring.interval_count === 1">
                      Monthly
                      </em>
                      <em v-else-if="plan.recurring && plan.recurring.interval === 'day'">
                      Daily
                      </em>
                      <em v-else>
                      Monthly
                      </em>
                    </span></h2
                  >
                  <div>
                    {{plan.desc}}
                  </div>
                </div>
                <h5 class="plan-name">{{ plan.name }}</h5>
              </div>
              
              <div class="plan-pricing mb-3">
                <div class="plan-price-main">
                  <span class="price-amount">${{ plan.amount }}</span>
                  <span class="price-period">
                    <span v-if="plan.type === 'one_time'">One Time</span>
                    <span v-else-if="plan.recurring.interval === 'year' && plan.recurring.interval_count === 1">/year</span>
                    <span v-else-if="plan.recurring.interval === 'month' && plan.recurring.interval_count === 1">/mo</span>
                    <span v-else-if="plan.recurring.interval === 'day'">/day</span>
                  </span>
                </div>
                <div v-if="plan.recurring && plan.recurring.interval === 'month'" class="plan-price-secondary">
                  ${{ (plan.amount * 3).toFixed(0) }} for 3 months
                </div>
              </div>
              
              <div class="plan-divider mb-3"></div>
              
              <div class="plan-features mb-4 flex-grow-1">
                <div class="plan-feature-item">
                  <div class="feature-icon">
                    <feather type="check-circle" />
                  </div>
                  <div class="feature-content">
                    <div class="feature-main">
                      <span
                        v-b-tooltip.hover
                        :title="plan.desc"
                        class="feature-text"
                      >
                        {{ plan.desc || 'Subscription plan' }}
                      </span>
                    </div>
                    <div v-if="plan.recurring && plan.recurring.interval === 'month'" class="feature-secondary">
                      Included in your plan
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="plan-action">
                <button
                  class="btn btn-subscribe w-100"
                  :class="{
                    'btn-primary': !(user.user_plans.length > 0 && user.user_plans.filter(o => plan.id === o.plan.stripe_price_id).length),
                    'btn-active': (user.user_plans.length > 0 && user.user_plans.filter(o => plan.id === o.plan.stripe_price_id).length),
                  }"
                  :disabled="
                    loadingSubscription !== null || planRowIsActive(plan)
                  "
                  @click="subscription(plan.id)"
                >
                  <b-spinner v-if="loadingSubscription === plan.id" small />
                  <span v-else>
                    <span v-if="planRowIsActive(plan)">Active</span>
                    <span v-else>
                      <i class="uil uil-arrow-right mr-1"></i>Purchase Now for ${{
                        plan.amount
                      }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center mt-5">
        <b-spinner large></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import AddCreditCard from '../Common/AddCreditCard'

export default {
  components: { AddCreditCard },
  props:{
    typeFilter:{
      default:null,
      type:String
    }
  },
  data() {
    return {
      showPaymentModal:false,
      loadingPlans: false,
      loadingSubscription: null,
      showAddCard: false,
      loadingCreateCard: false,
      selectedPlan: null,
      loadingPaymentMethod: false,
      loadingSetDefault: false,
      loadingStopImpersonate: false,
      errorMsg:null,
      recoveryModalAutoOpened: false,
      recoveryHydratedPlan: null,
      recoveryHydrating: false
    }
  },

  computed: {
    isImpersonating() {
      return this.$store.getters['auth/isImpersonating']
    },

    /** Full catalog list shown on subscription page. */
    catalogPlans() {
      let plans = this.$store.getters['subscription/allPlans'] || [];
      const userPlanIds = (this.user && this.user.user_plans)
        ? this.user.user_plans.map(up => up.plan && up.plan.id).filter(Boolean)
        : [];
      return plans.filter(
        (plan) => (
          this.typeFilter !== null
            ? plan.hasOwnProperty('db_plan') && plan.db_plan !== null && plan.db_plan.product_type === this.typeFilter
            : true
        ) && (
          plan.visible || plan.db_plan !== null && userPlanIds.includes(plan.db_plan.id)
        )
      );
    },
    /** Grid uses full catalog; popup decides previous RepuAgency plan separately. */
    plans() {
      return this.catalogPlans;
    },
    /** Stripe price to charge: purchase history — never unrelated dashboard/default tiers on Repuagency. */
    rawBillingStripePriceId() {
      if (!this.user) return null;

      if (this.isRepuagencySite()) {
        const repuRows = (this.user.user_plans || []).filter((up) => this.isRepuAgencyPurchaseHistoryRow(up));
        if (repuRows.length) {
          const pastDue = repuRows.find((up) => this.isPastDue(up));
          const pickPlan = pastDue || [...repuRows].sort((a, b) => {
            const ta = Date.parse(a.updated_at || '') || 0;
            const tb = Date.parse(b.updated_at || '') || 0;
            return tb - ta;
          })[0];
          if (pickPlan && pickPlan.plan && pickPlan.plan.stripe_price_id) {
            return pickPlan.plan.stripe_price_id;
          }
        }
        const priceMatchesPurchaseRow = (stripeId) =>
          stripeId &&
          (this.user.user_plans || []).some(
            (row) =>
              row.plan &&
              row.plan.stripe_price_id === stripeId &&
              this.isRepuAgencyPurchaseHistoryRow(row),
          );

        const fromSub = this.user.stripe_plan;
        const subStr = fromSub ? String(fromSub) : '';
        if (
          (subStr.startsWith('price_') || subStr.startsWith('plan_')) &&
          priceMatchesPurchaseRow(fromSub)
        ) {
          return fromSub;
        }
        const meta = this.user.last_plan_id;
        const metaStr = meta ? String(meta) : '';
        if (
          (metaStr.startsWith('price_') || metaStr.startsWith('plan_')) &&
          priceMatchesPurchaseRow(meta)
        ) {
          return meta;
        }
        return null;
      }

      /** Align billing SKU with `/profile` `recovery_plan_preview` when present so the modal plan strip stays in sync with the API preview. */
      const apiPv = this.user.recovery_plan_preview;
      const apiPvId =
        apiPv && apiPv.id !== undefined && apiPv.id !== null && String(apiPv.id).length
          ? String(apiPv.id)
          : '';
      if (
        apiPvId &&
        (apiPvId.startsWith('plan_') || apiPvId.startsWith('price_'))
      ) {
        return apiPv.id;
      }

      const fromSub = this.user.stripe_plan;
      const fromSubStr = fromSub ? String(fromSub) : '';
      if (fromSubStr.startsWith('price_')) return fromSub;

      const ups = this.user.user_plans || [];

      /** Latest Cashier `default` row (matches API `recovery_plan_preview`). */
      const cashierSkuRaw = this.user.recovery_default_stripe_plan;
      const cashierSku =
        cashierSkuRaw !== undefined && cashierSkuRaw !== null && String(cashierSkuRaw).length
          ? String(cashierSkuRaw)
          : '';
      if (
        cashierSku &&
        (cashierSku.startsWith('plan_') || cashierSku.startsWith('price_'))
      ) {
        if (cashierSku.startsWith('price_')) return cashierSku;
        const fromCashierHist = this.replyEngineRepuStripePriceFromHistory(ups);
        if (fromCashierHist) return fromCashierHist;
        if (this.replyEngineCatalogHasLegacyPlanId(cashierSku)) return cashierSku;
        return cashierSku;
      }

      const meta = this.user.last_plan_id;
      const metaStr = meta ? String(meta) : '';
      if (metaStr.startsWith('price_')) return meta;

      /**
       * Cashier often stores legacy `plan_*` on Subscription — same id may appear in `plans.stripe_price_id`.
       * Resolve this Stripe SKU before unrelated `past_due` pivots (e.g. leftover InstaStream flags).
       */
      if (fromSubStr.startsWith('plan_')) {
        const fromHistory = this.replyEngineRepuStripePriceFromHistory(ups);
        if (fromHistory) return fromHistory;
        if (this.replyEngineCatalogHasLegacyPlanId(fromSubStr)) return fromSubStr;
        return fromSubStr;
      }

      if (metaStr.startsWith('plan_')) {
        const fromHistory = this.replyEngineRepuStripePriceFromHistory(ups);
        if (fromHistory) return fromHistory;
        if (this.replyEngineCatalogHasLegacyPlanId(metaStr)) return metaStr;
        return metaStr;
      }

      const repuPastDue = ups.find((o) => {
        if (!this.isPastDue(o) || !o.plan || !o.plan.stripe_price_id) return false;
        const pt = o.product_type || (o.plan && o.plan.product_type);
        return pt === 'RepuAgency';
      });
      if (repuPastDue && repuPastDue.plan.stripe_price_id) return repuPastDue.plan.stripe_price_id;
      const anyPastDue = ups.find((o) => this.isPastDue(o) && o.plan && o.plan.stripe_price_id);
      if (anyPastDue && anyPastDue.plan.stripe_price_id) return anyPastDue.plan.stripe_price_id;

      return this.replyEngineRepuStripePriceFromHistory(ups) || meta || null;
    },
    /** Same Repuagency purchase rows as billing — never a catalog-only stripe merge. */
    repuagencyFallbackBillingPriceId() {
      if (!this.isRepuagencySite() || !this.user) return null;
      const rows = (this.user.user_plans || []).filter(
        (u) =>
          this.isRepuAgencyPurchaseHistoryRow(u) &&
          u.plan &&
          u.plan.stripe_price_id,
      );
      if (!rows.length) return null;
      const pastDueUp = rows.find((u) => this.isPastDue(u));
      const pick =
        pastDueUp ||
        [...rows].sort((a, b) => {
          const ta = Date.parse(a.updated_at || '') || 0;
          const tb = Date.parse(b.updated_at || '') || 0;
          return tb - ta;
        })[0];
      return pick && pick.plan ? pick.plan.stripe_price_id : null;
    },
    /** Only real purchase / subscription SKU — never `dashboard_products` or cheapest marketed plan. */
    recoverablePlanId() {
      const raw = this.rawBillingStripePriceId || (this.isRepuagencySite() ? this.repuagencyFallbackBillingPriceId : null);
      if (!raw) return null;
      const s = String(raw);
      if (this.isRepuagencySite() && !s.startsWith('price_') && !s.startsWith('plan_')) return null;
      return raw;
    },

    user() {
      return this.$store.getters['auth/user']
    },
    /** Payment failed / canceled recovery: show only when there was an existing subscription context (not expired trial alone). */
    recoveryEligible() {
      if (!this.user || !this.user.agency || this.user.agency.free_fee) return false;

      const problemStatuses = [
        'past_due',
        'unpaid',
        'canceled',
        'cancelled',
        'incomplete',
        'incomplete_expired',
      ];
      const stripeIsProblematic = (raw) => {
        if (raw === null || raw === undefined || raw === '') return false;
        const v = String(raw).toLowerCase().trim();
        return problemStatuses.includes(v);
      };

      /** Latest `default` Cashier row (matches `recovery_plan_preview`); catches cancel/past_due when `stripe_status` on user is stale/null. */
      if (stripeIsProblematic(this.user.recovery_default_subscription_status)) return true;

      // API sets subscribed=true for Cashier subscribed('default'), which still includes past_due / unpaid.
      if (this.user.subscribed) {
        if (this.user.on_trial && !this.hasPastDuePlan) return false;
        const st = this.user.stripe_status ? String(this.user.stripe_status).toLowerCase().trim() : '';
        const agencySt = this.user.agency.stripe_status
          ? String(this.user.agency.stripe_status).toLowerCase().trim()
          : '';
        const userStripeHealthy = st === 'active' || st === 'trialing';
        const agencyStripeHealthy = !agencySt || agencySt === 'active' || agencySt === 'trialing';
        if (userStripeHealthy && agencyStripeHealthy && !this.hasPastDuePlan) return false;
      }

      if (this.hasPastDuePlan) return true;
      if (this.user.last_plan_id && !this.user.subscribed) return true;

      if (stripeIsProblematic(this.user.stripe_status)) return true;
      if (stripeIsProblematic(this.user.agency && this.user.agency.stripe_status)) return true;

      return false;
    },
    showRecoveryBanner() {
      return this.recoveryEligible;
    },
    recoveryStripeNeedsPaymentRemediation() {
      if (!this.user) return false;
      const need = ['past_due', 'unpaid', 'incomplete'];
      const check = (raw) => {
        if (raw === null || raw === undefined || raw === '') return false;
        return need.includes(String(raw).toLowerCase().trim());
      };
      return (
        check(this.user.recovery_default_subscription_status) ||
        check(this.user.stripe_status) ||
        check(this.user.agency && this.user.agency.stripe_status)
      );
    },
    /** Pivot or Stripe unpaid/past-due incomplete → payment form + settle invoice flow. */
    recoveryRequiresPaymentUpdateForm() {
      return this.recoveryEligible && (this.hasPastDuePlan || this.recoveryStripeNeedsPaymentRemediation());
    },
    recoveryShowsStripeCardForm() {
      if (!this.recoveryEligible || !this.user) return false;
      if (this.recoveryRequiresPaymentUpdateForm) return true;
      return !this.user.default_payment_method;
    },
    /** Canceled (or stale) subscription with a usable default PM → one-click resubscribe, no forced card refresh. */
    recoveryCanceledResubscribeWithSavedPm() {
      return (
        this.recoveryEligible &&
        !this.recoveryRequiresPaymentUpdateForm &&
        !!this.user.default_payment_method
      );
    },
    recoveryPlanStatusBadgeText() {
      if (!this.recoveryEligible) return '';
      return this.recoveryRequiresPaymentUpdateForm ? 'Past due' : 'Canceled';
    },
    recoveryModalHeadline() {
      if (!this.recoveryEligible) return '';
      return this.recoveryRequiresPaymentUpdateForm
        ? "We couldn't process your payment"
        : 'Subscription canceled';
    },
    recoveryModalSubhead() {
      if (!this.recoveryEligible) return '';
      if (this.recoveryRequiresPaymentUpdateForm) {
        return 'Your account is on hold. Update your payment method and pay any open invoice to restore access.';
      }
      if (!this.user.default_payment_method) {
        return 'Add a payment method to resubscribe to your previous plan.';
      }
      return 'You can resubscribe with your saved payment method—no need to re-enter card details.';
    },
    recoveryModalFootnote() {
      if (!this.recoveryEligible) return '';
      if (this.recoveryShowsStripeCardForm && this.recoveryRequiresPaymentUpdateForm) {
        return 'Access is restored immediately after approval.';
      }
      if (this.recoveryShowsStripeCardForm && !this.recoveryRequiresPaymentUpdateForm) {
        return 'Your card will be charged when you subscribe.';
      }
      return 'You will be charged using your saved card when you subscribe.';
    },
    /** ReplyEngine only: true when recovery targets RepuAgency (pivot row or catalog `product_type`). */
    replyEngineShowsHistoricRepuPlan() {
      if (this.isRepuagencySite() || !this.user || !this.recoveryEligible) return false;
      const id = this.recoverablePlanId;
      if (!id) return false;
      const idStr = String(id);
      const pv = this.user.recovery_plan_preview;
      if (
        pv &&
        pv.product_type === 'RepuAgency' &&
        idStr !== '' &&
        String(pv.id) === idStr
      ) {
        return true;
      }
      const ups = this.user.user_plans || [];
      if (
        ups.some(
          (o) =>
            this.isRepuAgencyPurchaseHistoryRow(o) &&
            o.plan &&
            idStr !== '' &&
            o.plan.stripe_price_id === idStr,
        )
      ) {
        return true;
      }
      const catalog = this.findCatalogPlanByStripeSku(this.$store.getters['subscription/allPlans'] || [], idStr);
      const dbPt = catalog && catalog.db_plan && catalog.db_plan.product_type;
      if (dbPt === 'RepuAgency') return true;
      const h = this.recoveryHydratedPlan;
      if (
        h &&
        (h.product_type === 'RepuAgency' ||
          (h.db_plan && h.db_plan.product_type === 'RepuAgency'))
      ) {
        return String(h.id || '') === idStr;
      }
      return false;
    },
    hasPastDuePlan() {
      if (!this.user || !this.user.user_plans || !this.user.user_plans.length) return false;
      return this.user.user_plans.some((plan) => this.isPastDue(plan));
    },
    /** Modal title: recovery flow names prior SKU where we infer it from history or subdomain. */
    recoveryPlanModalLabel() {
      if (this.isRepuagencySite() && this.recoveryEligible) return 'Previous plan';
      if (this.replyEngineShowsHistoricRepuPlan) return 'Previous plan';
      return 'Current Plan';
    },
    recoverablePlan() {
      if (!this.user) return null;
      /** Repuagency payment recovery popup: ONLY plans tied to a Repuagency `users_plan` purchase row. */
      if (this.isRepuagencySite() && this.recoveryEligible) {
        return this.historicRepuagencyRecoverySnapshot();
      }

      if (
        !this.isRepuagencySite() &&
        this.recoveryEligible &&
        this.user.recovery_plan_preview &&
        this.user.recovery_plan_preview.id
      ) {
        return this.snapshotFromRecoveryPlanPreview(this.user.recovery_plan_preview);
      }

      const id = this.recoverablePlanId;
      if (!id) return null;

      const all = this.$store.getters['subscription/allPlans'] || [];

      const storePlan = this.findCatalogPlanByStripeSku(all, id);
      if (storePlan) return storePlan;
      const fromGrid = this.findCatalogPlanByStripeSku(this.plans || [], id);
      if (fromGrid) return fromGrid;

      const fromPivotOnly = this.buildRecoverySnapshotFromUserPivot(id);
      if (fromPivotOnly) return fromPivotOnly;
      const h = this.recoveryHydratedPlan;
      return h && String(h.id) === String(id) ? h : null;
    },
    defaultPaymentMethodDetails() {
      if (!this.user || !this.user.default_payment_method || !this.paymentMethods) return null;
      return this.paymentMethods.find((method) => method.id === this.user.default_payment_method) || null;
    },
    cardMaskText() {
      const method = this.defaultPaymentMethodDetails;
      if (!method || !method.last_four) return '';
      return `... ${method.last_four}`;
    },
    declineMessage() {
      if (!this.recoveryRequiresPaymentUpdateForm) return '';
      return 'Payment failed or is past due. Please update your payment method.';
    },
    modalPlanName() {
      if (this.recoverablePlan && this.recoverablePlan.name) return this.recoverablePlan.name;
      return 'Your plan';
    },
    modalPlanDescription() {
      if (this.recoverablePlan && this.recoverablePlan.desc) return this.recoverablePlan.desc;
      return '';
    },
    modalCtaText() {
      const dollar =
        this.recoverablePlan && this.recoverablePlan.amount
          ? this.recoverablePlan.amount
          : null;
      if (this.recoveryCanceledResubscribeWithSavedPm) {
        return dollar ? `Resubscribe — $${dollar}` : 'Resubscribe';
      }
      if (this.recoveryRequiresPaymentUpdateForm) {
        return dollar ? `Update & Pay $${dollar}` : 'Update payment method';
      }
      return dollar ? `Add payment method & subscribe — $${dollar}` : 'Add payment method & subscribe';
    },

    paymentMethods() {
      return this.$store.getters['paymentMethod/all']
    }

  },
  watch: {
    recoverablePlanId(to, from) {
      if (
        from !== undefined &&
        String(to || '') !== String(from || '')
      ) {
        this.recoveryHydratedPlan = null;
      }
      this.$nextTick(() => void this.ensureHydratedRecoveryPlan());
    },
    recoveryEligible(to) {
      if (!to) this.recoveryHydratedPlan = null;
      else this.$nextTick(() => void this.ensureHydratedRecoveryPlan());
    },
    showRecoveryBanner(isVisible) {
      if (isVisible) {
        this.openRecoveryModal();
      }
    },
    showPaymentModal(open) {
      if (open) {
        this.getPaymentMethods()
      }
    }
  },
  updated(){
    this.errorMessage();
  },
  mounted() {
    const afterPlans = async () => {
      await this.ensureHydratedRecoveryPlan();
      if (this.showRecoveryBanner) await this.openRecoveryModal();
    };

    const existingPlans = this.$store.getters['subscription/allPlans'];
    if (existingPlans && existingPlans.length) {
      void afterPlans();
    } else {
      this.loadingPlans = true;
      this.$store
        .dispatch('subscription/getAllPlans')
        .then(() => {
          this.loadingPlans = false;
          void afterPlans();
        })
        .catch(() => {
          this.loadingPlans = false;
          void afterPlans();
        });
    }
  },

  methods: {
    isRepuagencySite() {
      if (typeof window === 'undefined' || !window.location || !window.location.hostname) return false;
      return window.location.hostname.includes('repuagency.com');
    },
    /**
     * Match API hasRA semantics: pivot `product_type`, plan.product_type,
     * or planProducts[] — legacy tiers often omit product_type but set planProducts only.
     */
    isRepuAgencyPurchaseHistoryRow(up) {
      const db = up && up.plan;
      if (!db || !db.stripe_price_id) return false;
      if ((db.type || '').toLowerCase() === 'one_time') return false;

      let products = db.planProducts;
      if (typeof products === 'string') {
        try {
          products = JSON.parse(products);
        } catch (_) {
          products = null;
        }
      }

      const pivotPt = String(up.product_type || '');
      const dbPt = String(db.product_type || '');
      const normalized = (s) =>
        String(s || '')
          .toLowerCase()
          .replace(/[\s_]+/g, '');

      const isRepuName =
        normalized(pivotPt) === 'repuagency' ||
        normalized(dbPt) === 'repuagency' ||
        pivotPt === 'RepuAgency' ||
        dbPt === 'RepuAgency';

      let fromProducts = Array.isArray(products) && products.includes('RepuAgency');

      const label = `${db.name || ''} ${db.description || ''}`.toLowerCase();
      if (label.includes('lifetime')) return false;

      if (!isRepuName && !fromProducts) {
        if (label.includes('repuagency') || label.includes('repu agency')) {
          fromProducts = true;
        }
      }

      if (!(isRepuName || fromProducts)) return false;

      if (db.product_type && db.product_type !== 'RepuAgency') return false;

      if (
        label.includes('replyengine') ||
        label.includes('reply engine') ||
        label.includes('tapengine') ||
        label.includes('tap engine') ||
        label.includes('zensocial') ||
        label.includes('review surge') ||
        label.includes('reviewsurge')
      ) {
        return isRepuName || fromProducts;
      }

      return true;
    },
    /** Synthetic cards: every historic RepuAgency purchase + Stripe sub SKU if still RepuAgency (retired SKUs okay). */
    syntheticRepuagencyRecoveryPlans() {
      if (!this.isRepuagencySite() || !this.user) return [];
      const synth = [];
      const seenIds = new Set();
      const all = this.$store.getters['subscription/allPlans'] || [];

      const addFromDbPlan = (p) => {
        if (!p || !p.stripe_price_id || seenIds.has(p.stripe_price_id)) return;
        seenIds.add(p.stripe_price_id);
        const cents = Number(p.price);
        synth.push({
          id: p.stripe_price_id,
          name: p.name || 'RepuAgency',
          desc: p.description || '',
          amount: Number.isFinite(cents) ? String(cents / 100) : '0',
          type: 'recurring',
          recurring: { interval: 'month', interval_count: 1 },
          db_plan: {
            id: p.id,
            product_type: p.product_type || 'RepuAgency',
            stripe_price_id: p.stripe_price_id,
          },
          visible: true,
        });
      };

      for (const up of this.user.user_plans || []) {
        if (!this.isRepuAgencyPurchaseHistoryRow(up) || !up.plan) continue;
        addFromDbPlan(up.plan);
      }

      const stripePlanStr = this.user.stripe_plan ? String(this.user.stripe_plan) : '';
      const sp =
        stripePlanStr.startsWith('price_') || stripePlanStr.startsWith('plan_')
          ? this.user.stripe_plan
          : null;
      if (sp && !seenIds.has(sp)) {
        const matchUp = (this.user.user_plans || []).find((o) => o.plan && o.plan.stripe_price_id === sp);
        if (matchUp && this.isRepuAgencyPurchaseHistoryRow(matchUp)) {
          addFromDbPlan(matchUp.plan);
        } else {
          const catalog = all.find((p) => p.id === sp);
          if (catalog) {
            seenIds.add(sp);
            synth.push({ ...catalog });
          }
        }
      }

      return synth;
    },
    planRowIsActive(plan) {
      if (!this.user || !this.user.user_plans || !this.user.user_plans.length) return false;
      return this.user.user_plans.some(
        (up) => up.plan && up.plan.stripe_price_id === plan.id && !this.isPastDue(up),
      );
    },
    async openRecoveryModal() {
      if (this.recoveryModalAutoOpened) return;
      await this.ensureHydratedRecoveryPlan();
      this.recoveryModalAutoOpened = true;
      this.selectedPlan =
        this.recoverablePlanId ||
        (this.recoverablePlan ? this.recoverablePlan.id : null) ||
        null;
      this.getPaymentMethods();
      this.showPaymentModal = true;
    },
    isPastDue(plan) {
      if (!plan) return false;
      const raw = plan.past_due;
      if (raw === true || raw === 1 || raw === '1') return true;
      if (raw === false || raw === 0 || raw === '0' || raw === null || raw === undefined) return false;
      const normalized = String(raw).toLowerCase().trim();
      return ['true', 'yes', 'y', 'past_due', 'past due'].includes(normalized);
    },
    /**
     * Pick Repu recurring `stripe_price_id` from `users_plan` for ReplyEngine billing UI (legacy sub `plan_*` cannot match DB).
     */
    replyEngineRepuStripePriceFromHistory(ups) {
      const rows = (ups || []).filter(
        (o) =>
          this.isRepuAgencyPurchaseHistoryRow(o) &&
          o.plan &&
          o.plan.stripe_price_id &&
          String(o.plan.stripe_price_id).startsWith('price_'),
      );
      if (!rows.length) return null;
      const pastDueUp = rows.find((u) => this.isPastDue(u));
      const pick =
        pastDueUp ||
        [...rows].sort((a, b) => {
          const ta = Date.parse(a.updated_at || '') || 0;
          const tb = Date.parse(b.updated_at || '') || 0;
          return tb - ta;
        })[0];
      return pick && pick.plan ? pick.plan.stripe_price_id : null;
    },
    findCatalogPlanByStripeSku(all, stripeId) {
      if (!stripeId) return null;
      const list = all || [];
      const sid = String(stripeId);
      return (
        list.find((plan) => plan.id === sid) ||
        list.find((plan) => plan.db_plan && plan.db_plan.stripe_price_id === sid) ||
        null
      );
    },
    replyEngineCatalogHasLegacyPlanId(planId) {
      const p = planId ? String(planId) : '';
      if (!p.startsWith('plan_')) return false;
      return !!this.findCatalogPlanByStripeSku(this.$store.getters['subscription/allPlans'] || [], p);
    },
    snapshotFromRecoveryPlanPreview(pv) {
      if (!pv || !pv.id) return null;
      const isOneTime = String(pv.type || '').toLowerCase() === 'one_time';
      return {
        id: pv.id,
        name: pv.name || 'Your plan',
        desc: pv.desc || '',
        amount:
          pv.amount !== undefined && pv.amount !== null && String(pv.amount).length
            ? String(pv.amount)
            : undefined,
        type: isOneTime ? 'one_time' : 'recurring',
        recurring: isOneTime ? undefined : { interval: 'month', interval_count: 1 },
        visible: true,
        product_type: pv.product_type,
        db_plan: pv.db_plan || null,
      };
    },
    buildRecoverySnapshotFromUserPivot(stripeSku) {
      if (!this.user || !this.user.user_plans || stripeSku === null || stripeSku === undefined || stripeSku === '') {
        return null;
      }
      const sid = String(stripeSku);
      const up = this.user.user_plans.find((row) => row.plan && row.plan.stripe_price_id === sid);
      const p = up && up.plan;
      if (!p) return null;
      const cents = Number(p.price);
      const amount = Number.isFinite(cents) ? String(cents / 100) : undefined;
      return {
        id: sid,
        name: p.name || 'Your plan',
        desc: p.description || '',
        amount,
        type: 'recurring',
        recurring: { interval: 'month', interval_count: 1 },
        db_plan: { ...p },
        visible: true,
      };
    },
    hasLocalRecoveryPlanPreview(stripeSku) {
      if (stripeSku === null || stripeSku === undefined || String(stripeSku) === '') return false;
      const all = this.$store.getters['subscription/allPlans'] || [];
      if (this.findCatalogPlanByStripeSku(all, stripeSku)) return true;
      return !!this.buildRecoverySnapshotFromUserPivot(stripeSku);
    },
    normalizeHydratedRecoveryPlanFromApi(api) {
      if (!api || !api.success || !api.data) return null;
      const d = api.data;
      if (!d.id) return null;
      const rawAmt = d.amount;
      const amount =
        rawAmt !== undefined && rawAmt !== null && String(rawAmt).length
          ? Number(rawAmt).toFixed(2)
          : undefined;
      const t = d.type;
      const isOneTime =
        t === 'one_time' ||
        (typeof t === 'string' && (t.includes('one') || t === 'one-time'));
      return {
        id: d.id,
        name: d.name || 'Your plan',
        desc: d.desc || '',
        amount,
        type: isOneTime ? 'one_time' : 'recurring',
        recurring: isOneTime ? undefined : { interval: 'month', interval_count: 1 },
        visible: d.visible !== false,
        product_type: d.product_type || null,
        db_plan: d.db_plan || null,
      };
    },
    async ensureHydratedRecoveryPlan() {
      if (!this.recoveryEligible || !this.user) {
        this.recoveryHydratedPlan = null;
        return;
      }
      if (this.isRepuagencySite()) {
        this.recoveryHydratedPlan = null;
        return;
      }
      const id = this.recoverablePlanId;
      if (!id) {
        this.recoveryHydratedPlan = null;
        return;
      }
      if (this.hasLocalRecoveryPlanPreview(id)) {
        this.recoveryHydratedPlan = null;
        return;
      }
      if (this.recoveryHydratedPlan && String(this.recoveryHydratedPlan.id) === String(id)) return;
      this.recoveryHydrating = true;
      try {
        const payload = await this.$store.dispatch('subscription/getPlanByName', id);
        this.recoveryHydratedPlan = this.normalizeHydratedRecoveryPlanFromApi(payload);
      } catch (e) {
        this.recoveryHydratedPlan = null;
      } finally {
        this.recoveryHydrating = false;
      }
    },
    /**
     * Recovery modal on repuagency.com: show only SKU tied to verified Repuagency purchase (`users_plan` + historic rules).
     * No storefront catalog/default-tier cards here.
     */
    historicRepuagencyRecoverySnapshot() {
      if (!this.user || !this.user.user_plans) return null;

      const snapshotFromDbPlan = (p, stripePriceId) => {
        if (!p || !stripePriceId) return null;
        const cents = Number(p.price);
        return {
          id: stripePriceId,
          name: p.name || 'RepuAgency',
          desc: p.description || '',
          amount: Number.isFinite(cents) ? String(cents / 100) : undefined,
          type: 'recurring',
          recurring: { interval: 'month', interval_count: 1 },
          db_plan: { ...p },
          visible: true,
        };
      };

      const rowMatchingStripePrice = (stripeId) =>
        stripeId &&
        (this.user.user_plans || []).find(
          (row) =>
            row.plan &&
            row.plan.stripe_price_id === stripeId &&
            this.isRepuAgencyPurchaseHistoryRow(row),
        );

      const billId = this.recoverablePlanId;
      if (billId) {
        const up = rowMatchingStripePrice(billId);
        if (up && up.plan) return snapshotFromDbPlan(up.plan, billId);
        return null;
      }

      const historicRows = (this.user.user_plans || []).filter(
        (u) =>
          this.isRepuAgencyPurchaseHistoryRow(u) &&
          u.plan &&
          u.plan.stripe_price_id,
      );
      if (historicRows.length !== 1) return null;
      const p = historicRows[0].plan;
      return snapshotFromDbPlan(p, p.stripe_price_id);
    },
    errorMessage(){
      this.errorMsg = sessionStorage.getItem('sub-error-msg');
    },
    logout() {
      this.$store
        .dispatch('auth/logout')
        .then(() => {
          this.$router.push({ name: 'login' })
        })
        .catch(() => {})
    },

    onSuccessHandler() {
      if (this.user.agency) {
        if(this.typeFilter !== null){
          setTimeout(function(){
            // TODO: Setup events instead of relying on a timeout
            window.location.reload();
          },3000);
        }else {
          if(window.location.origin.includes('repuagency.com')){
            this.$router.push({name: 'agency.customers.index'})
          }else{
            this.$router.push({name: 'agency.dashboard'})
          }
        }
      } else {
        this.getPlans()
      }
    },

    onFailedHandler() {},

    getPlans() {
      if (this.$store.getters['subscription/allPlans']) return

      this.loadingPlans = true
      this.$store
        .dispatch('subscription/getAllPlans')
        .then(() => {
          this.loadingPlans = false
        })
        .catch(() => {
          this.loadingPlans = false
        })
    },

    subscription(plan) {
      this.selectedPlan = plan;
      /** Past-due / unpaid: reopen card flow. Canceled with saved PM: subscribe directly. */
      if (this.showRecoveryBanner && this.recoveryRequiresPaymentUpdateForm) {
        this.getPaymentMethods();
        this.showPaymentModal = true;
        return;
      }
      if(this.user.default_payment_method){
        this.loadingSubscription = plan

        this.$store
            .dispatch('subscription/subscribe', plan)
            .then(() => {
              let that = this;
              setTimeout(function(){
                // TODO: Setup events instead of relying on a timeout
                that.loadingSubscription = null
                window.location.reload();
              },3000);
            })
            .catch(() => {
              this.loadingSubscription = null
            })
      }else{
        this.getPaymentMethods();
        this.showPaymentModal = true;
      }

    },

    createPaymentMethod(paymentMethodId) {
      const planToSubscribe =
        this.selectedPlan ||
        this.recoverablePlanId ||
        (this.recoverablePlan && this.recoverablePlan.id)
      if (!planToSubscribe) {
        this.loadingSubscription = null
        this.showPaymentModal = false
        return
      }
      this.$store
        .dispatch('paymentMethod/create', paymentMethodId)
        .then(() => {
          this.$store
            .dispatch('subscription/subscribe', planToSubscribe)
            .then(() => {
              this.loadingSubscription = null
              this.onSuccessHandler()
            })
            .catch(() => {
              this.loadingSubscription = null
              this.getPlans()
            })
        })
        .catch(() => {
          this.loadingSubscription = null
        })
    },

    setDefaultPaymentMethod(paymentMethodId) {
      this.loadingSetDefault = true
      this.$store
        .dispatch('paymentMethod/setDefault', paymentMethodId)
        .then((res) => {
          this.$store
            .dispatch('subscription/resume')
            .then(() => {
              this.loadingSetDefault = false
              this.onSuccessHandler()
            })
            .catch(() => {
              this.loadingSetDefault = false
              this.getPlans()
            })
        })
        .catch((errors) => {
          this.loadingSetDefault = false
        })
    },

    getPaymentMethods() {
      this.loadingPaymentMethod = true
      this.$store
        .dispatch('paymentMethod/getAll')
        .then((res) => {
          this.loadingPaymentMethod = false
        })
        .catch(() => {
          this.loadingPaymentMethod = false
        })
    },

    onRecoveryModalPrimaryAction() {
      if (this.recoveryCanceledResubscribeWithSavedPm) {
        void this.onRecoveryResubscribeWithDefaultPm();
        return;
      }
      void this.validateCard();
    },
    onRecoveryResubscribeWithDefaultPm() {
      const plan =
        this.selectedPlan ||
        this.recoverablePlanId ||
        (this.recoverablePlan && this.recoverablePlan.id);
      if (!plan || !this.user.default_payment_method) return;
      this.loadingSubscription = true;
      this.$store
        .dispatch('subscription/subscribe', plan)
        .then(() => {
          this.loadingSubscription = null;
          this.showPaymentModal = false;
          this.onSuccessHandler();
        })
        .catch(() => {
          this.loadingSubscription = null;
          this.getPlans();
        });
    },
    async validateCard() {
      if (!this.$refs.cardForm) {
        this.loadingSubscription = null;
        return;
      }
      this.loadingSubscription = true;

      await this.$refs.cardForm
        .validate()
        .then((paymentMethodId) => {
          this.createPaymentMethod(paymentMethodId);
        })
        .catch(() => {
          this.loadingSubscription = null;
        })
    },

    stopImpersonate() {
      this.loadingStopImpersonate = true

      this.$store
        .dispatch('auth/stopImpersonate')
        .then(() => {
          this.$store
            .dispatch('auth/getProfile')
            .then((user) => {
              this.$router.push('/admin')
              this.loadingStopImpersonate = false
            })
            .catch(() => {
              this.loadingStopImpersonate = false
            })
        })
        .catch(() => {
          this.loadingStopImpersonate = false
        })
    },

  },
}
</script>

<style lang="scss">
.subscription-page {
  // Modal styles are in the global style block below.
  .recovery-banner {
    border: 1px solid #f1d5d9;
    background: linear-gradient(180deg, #fff5f5 0%, #ffffff 55%);
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 6px 24px rgba(34, 38, 53, 0.08);
  }
  .recovery-banner__header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .recovery-banner__icon {
    color: #d9534f;
    font-size: 18px;
    line-height: 1.2;
    margin-top: 2px;
  }
  .recovery-banner__title {
    font-weight: 700;
    color: #842029;
    margin-bottom: 2px;
  }
  .recovery-banner__text {
    color: #842029;
    opacity: 0.9;
  }
  .recovery-banner__plan {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: #4a4f63;
  }
  .recovery-banner__plan-label {
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: 600;
    color: #8d93a8;
    font-size: 11px;
  }
  .recovery-banner__plan-name {
    font-weight: 600;
    color: #2d3348;
  }
  .recovery-banner__plan-price {
    color: #d9534f;
    font-weight: 600;
  }
  .recovery-banner__cta {
    width: 100%;
    border: none;
    border-radius: 8px;
    background: #6d84e8;
    color: #fff;
    font-weight: 600;
    padding: 9px 12px;
  }
  .recovery-banner__cta:disabled {
    opacity: 0.75;
  }
  .payment-methods {
    .payment-method {
      padding: 10px 0;
      .card-brand {
        font-size: 16px;
        font-weight: 500;
        color: #2c2e2f;
        text-transform: uppercase;
      }
      .card-bumber,
      .card-expiration {
        font-weight: 500;
        color: #666;
      }
    }
  }
  .payment-methods .payment-method .actions {
    margin-top: 10px;
  }
  .payment-methods .payment-method .actions a[disabled] {
    opacity: 0.6;
  }
  .logout-button {
    max-width: 200px;
    position: absolute;
    top: 50px;
    right: 50px;
  }
}
</style>

<style lang="scss">
/* b-modal is mounted outside `.subscription-page`, so modal styles must be global. */
.subscription-recovery-modal .modal-dialog {
  max-width: 460px;
}

.subscription-recovery-modal__content {
  border: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(26, 32, 55, 0.2);
}

.subscription-recovery-modal .modal-header {
  background: #fff1f1;
  border-bottom: 1px solid #f1d5d9;
  padding: 14px 18px 12px;
}

.subscription-recovery-modal__body {
  padding: 14px 18px 16px;
  background: #fff;
}

.subscription-recovery-modal__header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 0 4px 2px 0;
}

.subscription-recovery-modal__header-main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.subscription-recovery-modal__header-icon {
  color: #df3e42;
  font-size: 18px;
  margin-top: 2px;
}

.subscription-recovery-modal__title {
  font-size: 20px;
  font-weight: 700;
  color: #17223b;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.subscription-recovery-modal__subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #5f667a;
}

.subscription-recovery-modal__close {
  border: 0;
  background: transparent;
  color: #8d93a8;
  font-size: 22px;
  line-height: 1;
  padding: 0;
}

.subscription-recovery-modal__plan {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin: 2px -18px 12px;
  padding: 12px 18px;
  border-bottom: 1px solid #e3e7f0;
}

.subscription-recovery-modal__plan-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 3px;
}

.subscription-recovery-modal__plan-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #5c6378;
}

.subscription-recovery-modal__plan-status {
  font-size: 11px;
  font-weight: 700;
  color: #1f2d4f;
}

.subscription-recovery-modal__plan-name {
  font-size: 20px;
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: #16223f;
}

.subscription-recovery-modal__plan-desc {
  margin-top: 3px;
  font-size: 12px;
  color: #6f768a;
}

.subscription-recovery-modal__plan-right {
  text-align: right;
  padding-top: 2px;
}

.subscription-recovery-modal__card-mask {
  font-weight: 700;
  color: #1c2848;
  font-size: 18px;
  letter-spacing: 0.04em;
}

.subscription-recovery-modal__decline {
  margin-top: 4px;
  color: #df3e42;
  font-size: 12px;
}

.subscription-recovery-modal__payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.subscription-recovery-modal__payment-title {
  font-size: 20px;
  line-height: 1.15;
  font-weight: 700;
  color: #1a2746;
}

.subscription-recovery-modal__secure {
  margin: 0;
  font-size: 12px;
  color: #68718c;
  display: flex;
  align-items: center;
  gap: 6px;
}

.subscription-recovery-modal__encrypt {
  margin: 10px 0 12px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  color: #5f667a;
  background: #f5f6fa;
  display: flex;
  align-items: center;
  gap: 6px;
}

.subscription-recovery-modal__encrypt i {
  color: #25b36b;
  font-size: 14px;
}

.subscription-recovery-modal__cta {
  border: none;
  border-radius: 8px;
  background: #6d84e8;
  color: #fff;
  font-weight: 600;
  padding: 11px 12px;
  box-shadow: 0 6px 14px rgba(109, 132, 232, 0.28);
}

.subscription-recovery-modal__cta:disabled {
  opacity: 0.75;
}

.subscription-recovery-modal__note {
  text-align: center;
  margin-top: 10px;
  color: #6f768a;
  font-size: 12px;
}
</style>
